// src/App.tsx
import React, { useEffect, useMemo, useState } from "react";
import Login from "./components/Login";
import ModeSelection from "./components/ModeSelection";
import Till from "./components/Till";
import KitchenDisplay from "./components/KitchenDisplay";
import Handheld from "./components/Handheld";
import Kiosk from "./components/Kiosk";
import CustomerDisplay from "./components/CustomerDisplay";

import { Capacitor } from "@capacitor/core";
import { CustomerDisplay as NativeCustomerDisplay } from "./plugins/customerDisplay";

import { supabase } from "./lib/supabase";
import type { Staff } from "./types";

// Customer display messaging (BroadcastChannel if available + postMessage fallback)
// Keep this for WEB popup route and for native-injected MessageEvent('message') too.
import { listenCustomerDisplay } from "./lib/customerDisplay";

type Store = {
  id: string;
  name: string;
  location: string;
  logo?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
};

type StoreUserRow = {
  store_id: string | null;
};

const LS_SELECTED_STORE = "forkcast:selected_store_id";
const LS_SELECTED_MODE = "forkcast:selected_mode";

function isValidUuid(v: string | null | undefined): v is string {
  if (!v) return false;
  if (v === "null") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

function withTimeout<T>(p: Promise<T>, ms = 8000, label = "timeout"): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(label)), ms);
    p.then((v) => {
      window.clearTimeout(t);
      resolve(v);
    }).catch((e) => {
      window.clearTimeout(t);
      reject(e);
    });
  });
}

function makeFallbackStaff(storeId: string, email: string | null): Staff {
  return {
    id: "00000000-0000-0000-0000-000000000000",
    store_id: storeId,
    name: email ? email.split("@")[0] : "Manager",
    role: "manager",
    email: email ?? undefined,
    phone: "",
    start_date: new Date().toISOString().slice(0, 10),
    shifts: [],
    created_at: new Date().toISOString(),
    hourly_rate: 0,
    permissions: {
      can_void: true,
      can_discount: true,
      can_refund: true,
      can_manage_staff: true,
    },
  } as unknown as Staff;
}

/* -----------------------------
   POS UI helpers (styling only)
------------------------------ */

function Shell({
  children,
  title,
  subtitle,
  right,
}: {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      {(title || right) && (
        <div className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/80 backdrop-blur">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              {title && <div className="text-base sm:text-lg font-semibold tracking-tight truncate">{title}</div>}
              {subtitle && <div className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</div>}
            </div>
            {right && <div className="shrink-0">{right}</div>}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">{children}</div>
    </div>
  );
}

function CenterCard({
  title,
  description,
  children,
}: {
  title?: string;
  description?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-xl">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 p-6 sm:p-7">
          {title && <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{title}</h1>}
          {description && <p className="mt-2 text-sm sm:text-base text-slate-600">{description}</p>}
          {children && <div className="mt-6">{children}</div>}
        </div>
      </div>
    </div>
  );
}

function SecondaryButton({
  children,
  onClick,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold " +
        "bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 " +
        "focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:ring-offset-2 transition " +
        className
      }
    >
      {children}
    </button>
  );
}

function DangerButton({
  children,
  onClick,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold " +
        "bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:bg-rose-800 " +
        "focus:outline-none focus:ring-2 focus:ring-rose-300/70 focus:ring-offset-2 transition " +
        className
      }
    >
      {children}
    </button>
  );
}

function LoadingScreen({ label }: { label?: string }) {
  return (
    <Shell title="Forkcast POS" subtitle="Preparing your session…">
      <CenterCard title={label ?? "Loading…"} description="One moment while we get everything ready.">
        <div className="mt-2 flex items-center gap-3">
          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-900 animate-spin" />
          <div className="text-sm text-slate-600">Please wait</div>
        </div>
      </CenterCard>
    </Shell>
  );
}

/* -----------------------------
   Customer display route detection
------------------------------ */

function isCustomerDisplayPage(): boolean {
  const path = window.location.pathname || "";
  if (path === "/customer-display") return true;

  const hash = window.location.hash || "";
  return hash.includes("customer-display") || hash.includes("/customer");
}

/* -----------------------------
   Native customer display safe open
------------------------------ */

async function tryOpenNativeCustomerDisplaySafely() {
  // Only Android is relevant for your Presentation plugin.
  if (Capacitor.getPlatform() !== "android") return;

  // If the plugin didn't get registered/compiled, Capacitor will say "not implemented".
  // Guard first so we don't crash the app flow.
  const isAvailable =
    typeof (Capacitor as any).isPluginAvailable === "function"
      ? (Capacitor as any).isPluginAvailable("CustomerDisplay")
      : true;

  if (!isAvailable) {
    console.warn('CustomerDisplay plugin not available (isPluginAvailable=false).');
    return;
  }

  try {
    const res: any = await NativeCustomerDisplay.getDisplays();
    const displays = res?.displays ?? [];
    if (!Array.isArray(displays) || displays.length === 0) {
      console.warn("No secondary displays detected. Skipping customer display open.");
      return;
    }

    // This MUST match isCustomerDisplayPage() logic
    await NativeCustomerDisplay.open({ path: "/customer-display?screen=customer" });
    console.log("Customer display opened");
  } catch (e: any) {
    // Never let this bubble into an unhandled rejection.
    console.error("Customer display failed (non-fatal):", e);

    // If it's the classic "not implemented", it means Android did not load the plugin class.
    // Keep running POS anyway.
    const msg = String(e?.message || e || "");
    if (msg.toLowerCase().includes("not implemented")) {
      console.error(
        'CustomerDisplay "not implemented" means the native plugin was not registered/compiled into the Android app.'
      );
    }
  }
}

/* -----------------------------
   App
------------------------------ */

export default function App() {
  // Customer display: short-circuit the whole app
  // IMPORTANT: do this before any heavy auth/store loading to keep it fast and stable.
  if (isCustomerDisplayPage()) return <CustomerDisplayRoute />;

  const [selectedMode, setSelectedMode] = useState<string | null>(() => {
    const saved = localStorage.getItem(LS_SELECTED_MODE);
    return saved && saved !== "null" ? saved : null;
  });

  // Native customer screen: open only once, after mode selection
  const [customerOpened, setCustomerOpened] = useState(false);

  // Auth
  const [sessionReady, setSessionReady] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Stores
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [activeStoreId, setActiveStoreId] = useState<string | null>(() => {
    const saved = localStorage.getItem(LS_SELECTED_STORE);
    return isValidUuid(saved) ? saved : null;
  });

  // Staff row (best-effort mapping via email)
  const [staffLoading, setStaffLoading] = useState(false);
  const [currentStaff, setCurrentStaff] = useState<Staff | null>(null);

  async function loadStoresForUser(uid: string) {
    setStoresLoading(true);
    try {
      const { data: su, error: suErr } = await withTimeout(
        supabase.from("store_users").select("store_id").eq("user_id", uid),
        8000,
        "store_users timeout"
      );

      if (suErr) {
        console.error("store_users load error:", suErr);
        setStores([]);
        setActiveStoreId(null);
        localStorage.removeItem(LS_SELECTED_STORE);
        return;
      }

      const storeIds = (su as StoreUserRow[])
        .map((r) => r.store_id)
        .filter((x): x is string => Boolean(x) && x !== "null");

      if (storeIds.length === 0) {
        setStores([]);
        setActiveStoreId(null);
        localStorage.removeItem(LS_SELECTED_STORE);
        return;
      }

      const { data: st, error: stErr } = await withTimeout(
        supabase.from("stores").select("id,name,location,logo,email,phone,status").in("id", storeIds),
        8000,
        "stores timeout"
      );

      if (stErr) {
        console.error("stores load error:", stErr);
        setStores([]);
        setActiveStoreId(null);
        localStorage.removeItem(LS_SELECTED_STORE);
        return;
      }

      const storeList = (st ?? []) as Store[];
      setStores(storeList);

      if (storeList.length === 1) {
        setActiveStoreId(storeList[0].id);
        localStorage.setItem(LS_SELECTED_STORE, storeList[0].id);
      } else {
        setActiveStoreId((prev) => {
          if (prev && storeList.some((s) => s.id === prev)) {
            localStorage.setItem(LS_SELECTED_STORE, prev);
            return prev;
          }
          localStorage.removeItem(LS_SELECTED_STORE);
          return null;
        });
      }
    } catch (e) {
      console.error("loadStoresForUser exception:", e);
      setStores([]);
      setActiveStoreId(null);
      localStorage.removeItem(LS_SELECTED_STORE);
    } finally {
      setStoresLoading(false);
    }
  }

  // 1) Session bootstrap + auth listener (DO NOT BLOCK sessionReady on store loads)
  useEffect(() => {
    let mounted = true;

    // failsafe: never hang forever
    const failSafe = window.setTimeout(() => {
      if (!mounted) return;
      console.warn("Failsafe: forcing sessionReady=true after timeout");
      setSessionReady(true);
    }, 5000);

    (async () => {
      try {
        const { data, error } = await withTimeout(supabase.auth.getSession(), 8000, "getSession timeout");
        if (!mounted) return;

        if (error) {
          console.error("getSession error:", error);
          setUserId(null);
          setUserEmail(null);
          return;
        }

        const uid = data.session?.user?.id ?? null;
        const email = data.session?.user?.email ?? null;

        setUserId(uid);
        setUserEmail(email);

        // load stores in background (don’t await)
        if (uid) {
          loadStoresForUser(uid).catch((e) => console.error("loadStoresForUser bg error:", e));
        }
      } catch (e) {
        console.error("bootstrap exception:", e);
        if (!mounted) return;
        setUserId(null);
        setUserEmail(null);
      } finally {
        if (!mounted) return;
        setSessionReady(true);
        window.clearTimeout(failSafe);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      const uid = newSession?.user?.id ?? null;
      const email = newSession?.user?.email ?? null;

      setUserId(uid);
      setUserEmail(email);

      if (event === "SIGNED_OUT") {
        setSelectedMode(null);
        localStorage.removeItem(LS_SELECTED_MODE);

        setStores([]);
        setActiveStoreId(null);
        localStorage.removeItem(LS_SELECTED_STORE);

        setCurrentStaff(null);

        // also reset customer screen state
        setCustomerOpened(false);
        return;
      }

      // DON’T reset mode on SIGNED_IN/TOKEN_REFRESHED
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && uid) {
        if (stores.length === 0 && !storesLoading) {
          loadStoresForUser(uid).catch((e) => console.error("loadStoresForUser auth error:", e));
        }
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(failSafe);
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Load staff for selected store (or fallback manager)
  useEffect(() => {
    let cancelled = false;

    async function loadStaffForStore(storeId: string, email: string | null) {
      setStaffLoading(true);
      try {
        if (!isValidUuid(storeId)) {
          if (!cancelled) setCurrentStaff(null);
          return;
        }

        if (!email) {
          if (!cancelled) setCurrentStaff(makeFallbackStaff(storeId, null));
          return;
        }

        const { data: stf, error: stfErr } = await withTimeout(
          supabase
            .from("staff")
            .select("id,store_id,name,role,email,phone,start_date,shifts,created_at,hourly_rate")
            .eq("store_id", storeId)
            .eq("email", email)
            .limit(1)
            .maybeSingle(),
          8000,
          "staff timeout"
        );

        if (stfErr) {
          console.error("staff lookup error:", stfErr);
          if (!cancelled) setCurrentStaff(makeFallbackStaff(storeId, email));
          return;
        }

        if (stf) {
          const normalized = {
            ...(stf as any),
            shifts: Array.isArray((stf as any).shifts) ? (stf as any).shifts : [],
            permissions: (stf as any).permissions ?? undefined,
          };
          if (!cancelled) setCurrentStaff(normalized as Staff);
          return;
        }

        if (!cancelled) setCurrentStaff(makeFallbackStaff(storeId, email));
      } catch (e) {
        console.error("loadStaffForStore exception:", e);
        if (!cancelled) setCurrentStaff(makeFallbackStaff(storeId, email));
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    }

    if (!activeStoreId) {
      setCurrentStaff(null);
      return;
    }

    loadStaffForStore(activeStoreId, userEmail);

    return () => {
      cancelled = true;
    };
  }, [activeStoreId, userEmail]);

  // 3) On focus/visibility, don’t wipe userId; only refresh if we’re missing data
  useEffect(() => {
    const refresh = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const uid = data.session?.user?.id ?? null;
        const email = data.session?.user?.email ?? null;

        if (uid) {
          setUserId(uid);
          setUserEmail(email);
          if (stores.length === 0 && !storesLoading) {
            loadStoresForUser(uid).catch((e) => console.error("loadStoresForUser focus error:", e));
          }
        }
      } catch (e) {
        console.error("focus refresh exception:", e);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores.length, storesLoading]);

  // 4) Open native customer display once (Android only), after mode selection
  //    IMPORTANT: This must never crash the app if the plugin is missing/buggy.
  useEffect(() => {
    if (isCustomerDisplayPage()) return;
    if (Capacitor.getPlatform() !== "android") return;
    if (customerOpened) return;
    if (!selectedMode) return;

    let cancelled = false;

    (async () => {
      try {
        await tryOpenNativeCustomerDisplaySafely();
        if (!cancelled) setCustomerOpened(true);
      } catch (e) {
        // Should never happen because tryOpenNativeCustomerDisplaySafely already catches,
        // but keep this anyway.
        console.error("Unexpected customer display open error:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedMode, customerOpened]);

  const handleModeSelection = (mode: string) => {
    setSelectedMode(mode);
    localStorage.setItem(LS_SELECTED_MODE, mode);
  };

  const handleLogout = async () => {
    try {
      // Close native customer display if open
      if (Capacitor.getPlatform() === "android") {
        try {
          await NativeCustomerDisplay.close();
        } catch (e) {
          console.warn("Failed to close customer display:", e);
        }
      }

      await supabase.auth.signOut();
    } finally {
      setSelectedMode(null);
      localStorage.removeItem(LS_SELECTED_MODE);

      setStores([]);
      setActiveStoreId(null);
      localStorage.removeItem(LS_SELECTED_STORE);

      setCurrentStaff(null);
      setUserId(null);
      setUserEmail(null);

      setCustomerOpened(false);
    }
  };

  // ---------- UI states ----------
  if (!sessionReady) return <LoadingScreen label="Starting POS…" />;

  if (!userId) {
    return (
      <Shell title="Forkcast POS" subtitle="Sign in to continue">
        <div className="min-h-[70vh] flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 p-6 sm:p-7">
              <Login onLogin={() => {}} />
            </div>
            <div className="mt-3 text-center text-xs text-slate-500">Secure login powered by Supabase</div>
          </div>
        </div>
      </Shell>
    );
  }

  if (storesLoading) return <LoadingScreen label="Loading stores…" />;

  if (stores.length === 0) {
    return (
      <Shell title="Forkcast POS" subtitle="No store access">
        <CenterCard
          title="No store access"
          description="Your account isn’t linked to any store yet. Add a row in store_users for your user."
        >
          <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-sm text-slate-700">
            Table: <code className="px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200">store_users</code>{" "}
            — columns: <code className="px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200">user_id</code>,{" "}
            <code className="px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200">store_id</code>
          </div>

          <div className="mt-6 flex gap-3">
            <DangerButton className="w-full" onClick={handleLogout}>
              Sign out
            </DangerButton>
          </div>
        </CenterCard>
      </Shell>
    );
  }

  if (!activeStoreId) {
    return (
      <StorePicker
        stores={stores}
        onSelect={(id) => {
          setActiveStoreId(id);
          localStorage.setItem(LS_SELECTED_STORE, id);
        }}
        onLogout={handleLogout}
        userEmail={userEmail}
      />
    );
  }

  if (staffLoading) return <LoadingScreen label="Loading staff…" />;

  if (!selectedMode) {
    const activeStore = stores.find((s) => s.id === activeStoreId);
    return (
      <Shell
        title="Forkcast POS"
        subtitle={activeStore ? `${activeStore.name} • ${activeStore.location}` : "Choose a mode"}
        right={
          <div className="flex items-center gap-2">
            <SecondaryButton
              onClick={() => {
                setActiveStoreId(null);
                localStorage.removeItem(LS_SELECTED_STORE);
              }}
            >
              Change store
            </SecondaryButton>
            <DangerButton onClick={handleLogout}>Sign out</DangerButton>
          </div>
        }
      >
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 p-4 sm:p-6">
          <ModeSelection
            currentStaff={currentStaff}
            onModeSelect={handleModeSelection}
            onLogout={handleLogout}
            stores={stores}
            activeStoreId={activeStoreId}
            onSelectStore={(id) => {
              setActiveStoreId(id);
              localStorage.setItem(LS_SELECTED_STORE, id);
            }}
          />
        </div>
      </Shell>
    );
  }

  const renderSelectedMode = () => {
    const staff = currentStaff ?? makeFallbackStaff(activeStoreId, userEmail);

    switch (selectedMode) {
      case "till":
        return <Till currentStaff={staff} storeId={activeStoreId} onLogout={handleLogout} />;
      case "kds":
        return <KitchenDisplay currentStaff={staff} onLogout={handleLogout} storeId={activeStoreId} />;
      case "handheld":
        return <Handheld currentStaff={staff} onLogout={handleLogout} storeId={activeStoreId} />;
      case "kiosk":
        return <Kiosk storeId={activeStoreId} />;
      default:
        return <Till currentStaff={staff} storeId={activeStoreId} onLogout={handleLogout} />;
    }
  };

  return <div className="min-h-screen bg-slate-50 text-slate-900">{renderSelectedMode()}</div>;
}

/* -----------------------------
   Store picker
------------------------------ */

function StorePicker({
  stores,
  onSelect,
  onLogout,
  userEmail,
}: {
  stores: Store[];
  onSelect: (storeId: string) => void;
  onLogout: () => void;
  userEmail: string | null;
}) {
  return (
    <Shell
      title="Forkcast POS"
      subtitle={userEmail ? `Signed in as ${userEmail}` : "Choose a store"}
      right={<DangerButton onClick={onLogout}>Sign out</DangerButton>}
    >
      <CenterCard title="Choose a store" description="You have access to multiple stores. Pick one to continue.">
        <div className="space-y-3">
          {stores.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={
                "w-full text-left p-4 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm " +
                "hover:ring-slate-300 hover:bg-slate-50 active:bg-slate-100 transition"
              }
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                  <div className="text-sm text-slate-600 truncate">{s.location}</div>
                </div>
                <div className="shrink-0 text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-1">
                  Select
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {s.status ? (
                  <span className="rounded-full bg-slate-100 px-2 py-1 ring-1 ring-slate-200">{s.status}</span>
                ) : null}
                {s.phone ? (
                  <span className="rounded-full bg-slate-100 px-2 py-1 ring-1 ring-slate-200">{s.phone}</span>
                ) : null}
                {s.email ? (
                  <span className="rounded-full bg-slate-100 px-2 py-1 ring-1 ring-slate-200">{s.email}</span>
                ) : null}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <SecondaryButton className="w-full" onClick={onLogout}>
            Sign out
          </SecondaryButton>
        </div>
      </CenterCard>
    </Shell>
  );
}

/* -----------------------------
   Customer display route
------------------------------ */

function CustomerDisplayRoute() {
  const [orderData, setOrderData] = useState({
    cart: [],
    totalAmount: 0,
    customerName: "",
    tableNumber: "",
    orderType: "",
  });

  useEffect(() => {
    const stop = listenCustomerDisplay((msg: any) => {
      if (msg?.type === "UPDATE_ORDER") {
        const p = msg.payload ?? msg;
        setOrderData({
          cart: p.cart || [],
          totalAmount: p.totalAmount || 0,
          customerName: p.customerName || "",
          tableNumber: p.tableNumber || "",
          orderType: p.orderType || "",
        });
      }

      if (msg?.type === "CLEAR_ORDER") {
        setOrderData({
          cart: [],
          totalAmount: 0,
          customerName: "",
          tableNumber: "",
          orderType: "",
        });
      }
    });

    return stop;
  }, []);

  return (
    <CustomerDisplay
      cart={orderData.cart as any}
      totalAmount={orderData.totalAmount}
      customerName={orderData.customerName}
      tableNumber={orderData.tableNumber}
      orderType={orderData.orderType}
    />
  );
}
